import {Typography} from '@mui/material';
import Box from '@mui/material/Box';
import ArrIconButton from '../common/arr-icon-button/ArrIconButton';
import {ReactComponent as LeftIconDisabled} from '../../assets/images/prizmdoc-icons/Icon_left.svg';
import {ReactComponent as ZoomIn} from '../../assets/images/prizmdoc-icons/add.svg';
import {ReactComponent as ZoomOut} from '../../assets/images/prizmdoc-icons/subtract.svg';
import {ReactComponent as CopyTextIcon} from '../../assets/images/prizmdoc-icons/copy_text.svg';
import {ReactComponent as DownloadIcon} from '../../assets/images/prizmdoc-icons/download.svg';
import {ReactComponent as RightIcon} from '../../assets/images/prizmdoc-icons/Icon_right.svg';
import {ReactComponent as FirstPageDisabled} from '../../assets/images/prizmdoc-icons/page_first.svg';
import {ReactComponent as LastPageIcon} from '../../assets/images/prizmdoc-icons/page_last.svg';
import {ReactComponent as PanIcon} from '../../assets/images/prizmdoc-icons/pan.svg';
import {ReactComponent as PrintIcon} from '../../assets/images/prizmdoc-icons/print.svg';
import {ReactComponent as RotateLeftIcon} from '../../assets/images/prizmdoc-icons/rotate_left.svg';
import {ReactComponent as RotateRightIcon} from '../../assets/images/prizmdoc-icons/rotate_right.svg';
import {ReactComponent as Thumbnailicon} from '../../assets/images/prizmdoc-icons/thumbnail.svg';
import './DocToolBar.scss';
import ArrTextField from '../common/arr-text-field/ArrTextField';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  MouseToolType,
  truncateToFiveDecimals,
} from '../document-viewer/docViewerUtils';
import {useGetDocDownloadFormats} from '../../hooks/document/useDocument';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../common/arr-menu/ArrMenu';
import {
  getDownloadDocument,
  postPrintDocument,
} from '../../api/endpoints/documentApi';
import {
  IGetDownloadDocumentParams,
  IPrintDocumentRequest,
} from '../../api/types/document.type';
import {DocForPreview} from '../doc-explorer/doc-explorer-data-grid/DocExplorerDataGrid';
import {DownloadFormatType} from '../document-viewer/doc-navigation/DocNavigation';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../shared/constants';
import {t} from 'i18next';

const DEFAULT_ZOOM = 100;
const HUNDRED = 100;
const ZOOM_CHANGE_DELTA = 25;

interface DocToolbarProps {
  viewerControl: ViewerControl;
  totalPages: number;
  pageNumber: number;
  initialScaleFactor: number;
  isReady: boolean;
  documentDetails: DocForPreview;
}

type PrintAndDownloadProps = {
  documentDetails: DocForPreview | null;
};

export const DocToolbar = ({
  viewerControl,
  totalPages,
  pageNumber,
  isReady,
  initialScaleFactor,
  documentDetails,
}: DocToolbarProps) => {
  const [isPanMode, setIsPanMode] = useState(true);
  const PageNavigation = ({
    viewerControl,
    totalPages,
    pageNumber,
  }: {
    viewerControl: ViewerControl;
    totalPages: number;
    pageNumber: number;
  }) => {
    const [value, setValue] = useState(pageNumber);

    useEffect(() => {
      setValue(pageNumber);
    }, [pageNumber]);

    const previousDisabled = useMemo(() => pageNumber === 1, [pageNumber]);
    const nextDisabled = useMemo(
      () => pageNumber === totalPages,
      [totalPages, pageNumber]
    );

    return (
      <Box className="row-container">
        <Box className="small-gap-wrapper">
          <ArrIconButton
            shape="default"
            icon={
              previousDisabled ? (
                <FirstPageDisabled />
              ) : (
                <LastPageIcon className="rotate-180" />
              )
            }
            disabled={previousDisabled}
            ariaLabel={'First Page'}
            onClick={() => viewerControl?.changeToFirstPage()}
            tooltipContent="First Page"
            testId="first-page"
          />
          <ArrIconButton
            shape="default"
            icon={
              previousDisabled ? (
                <LeftIconDisabled />
              ) : (
                <RightIcon className="rotate-180" />
              )
            }
            disabled={previousDisabled}
            onClick={() => viewerControl?.changeToPrevPage()}
            ariaLabel={'Previous'}
            tooltipContent="Previous"
            testId="previous-page"
          />
        </Box>
        <Box className="input-container">
          <ArrTextField
            id="enabled-default-small"
            sizeVariant="small"
            dataTestId="doc-toolbar-page-textfield"
            aria-label="Change Page"
            value={value}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/[^0-9]/g, '');
              setValue(Number(onlyNums));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                viewerControl?.setPageNumber(value);
              }
            }}
            slotProps={{inputLabel: {shrink: false}}}
          />
        </Box>
        <Typography
          className="typo-text body4_size dark-text"
          data-testid="total-page-count"
        >
          {`of ${totalPages} pages`}
        </Typography>
        <Box className="small-gap-wrapper">
          <ArrIconButton
            shape="default"
            icon={
              nextDisabled ? (
                <LeftIconDisabled className="rotate-180" />
              ) : (
                <RightIcon />
              )
            }
            ariaLabel={'Next'}
            tooltipContent="Next"
            disabled={nextDisabled}
            onClick={() => viewerControl?.changeToNextPage()}
            testId="next-page"
          />
          <ArrIconButton
            shape="default"
            icon={
              nextDisabled ? (
                <FirstPageDisabled className="rotate-180" />
              ) : (
                <LastPageIcon />
              )
            }
            ariaLabel={'Last Page'}
            disabled={nextDisabled}
            onClick={() => viewerControl?.changeToLastPage()}
            tooltipContent="Last Page"
            testId="last-page"
          />
        </Box>
      </Box>
    );
  };

  const ZoomControls = useCallback(
    ({
      viewerControl,
      isReady,
    }: {
      viewerControl: ViewerControl;
      isReady: boolean;
    }) => {
      const [value, setValue] = useState(DEFAULT_ZOOM);
      const minValue = isReady ? viewerControl?.getMinScaleFactor() : 0.5;
      const maxValue = isReady ? viewerControl?.getMaxScaleFactor() : 1;
      const scaleValue = (value / HUNDRED) * initialScaleFactor;
      const getPercentFromScale = (scale: number) =>
        (HUNDRED / initialScaleFactor) * scale;

      const getScaleFromPercent = (percent: number) =>
        (percent / HUNDRED) * initialScaleFactor;

      return (
        <Box className="row-container medium-gap">
          <ArrIconButton
            shape="default"
            icon={<ZoomOut />}
            ariaLabel={'Zoom Out'}
            tooltipContent="Zoom Out"
            disabled={
              truncateToFiveDecimals(scaleValue) <=
              truncateToFiveDecimals(minValue)
            }
            testId="zoom-out"
            onClick={() => {
              //Previous multiple of 25
              const newZoom =
                Math.ceil((value - ZOOM_CHANGE_DELTA) / ZOOM_CHANGE_DELTA) *
                ZOOM_CHANGE_DELTA;

              // new actual Zoom Level
              const newScaleValue = Math.max(
                getScaleFromPercent(newZoom),
                minValue
              );

              viewerControl.setScaleFactor(newScaleValue);
              //New actual Percent
              setValue(
                newScaleValue === minValue
                  ? getPercentFromScale(newScaleValue)
                  : newZoom
              );
            }}
          />
          <Box className="input-container">
            <ArrTextField
              id="enabled-default-small"
              sizeVariant="small"
              dataTestId="doc-toolbar-zoom-textfield"
              aria-label="Zoom"
              value={Math.trunc(value)}
              onChange={(e) => {
                const onlyNums = e.target.value.replace(/[^0-9]/g, '');
                setValue(Number(onlyNums));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  let restrictedScaleValue = getScaleFromPercent(value);
                  if (restrictedScaleValue < minValue) {
                    restrictedScaleValue = minValue;
                  } else if (restrictedScaleValue > maxValue) {
                    restrictedScaleValue = maxValue;
                  }
                  viewerControl.setScaleFactor(restrictedScaleValue);
                  if (
                    restrictedScaleValue === minValue ||
                    restrictedScaleValue === maxValue
                  ) {
                    setValue(getPercentFromScale(restrictedScaleValue));
                  }
                }
              }}
              slotProps={{inputLabel: {shrink: false}}}
            />
          </Box>
          <ArrIconButton
            shape="default"
            icon={<ZoomIn />}
            ariaLabel={'Zoom In'}
            tooltipContent="Zoom In"
            testId="zoom-in"
            disabled={
              truncateToFiveDecimals(scaleValue) >=
              truncateToFiveDecimals(maxValue)
            }
            onClick={() => {
              //Next multiple of 25
              const newZoom =
                Math.floor((value + ZOOM_CHANGE_DELTA) / ZOOM_CHANGE_DELTA) *
                ZOOM_CHANGE_DELTA;
              // new actual Zoom Level
              const newScaleValue = Math.min(
                getScaleFromPercent(newZoom),
                maxValue
              );
              viewerControl.setScaleFactor(newScaleValue);

              //New actual Percent
              setValue(
                newScaleValue === maxValue
                  ? getPercentFromScale(newScaleValue)
                  : newZoom
              );
            }}
          />
        </Box>
      );
    },
    [viewerControl, isReady]
  );

  const PrintAndDownload = useCallback(
    ({documentDetails}: PrintAndDownloadProps) => {
      const menuRef = useRef<ArrMenuHandle | null>(null);
      const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
      const [showPrintToast, setShowPrintToast] = useState<boolean>(false);

      const {data: docDownloadFormats = [], refetch: getDocDownloadFormats} =
        useGetDocDownloadFormats(
          `formats?docId=${documentDetails?.id}&type=DOWNLOAD`
        );

      let actionMenuItems: CustomMenuItem[] = [];

      const actionDownloadOptions: CustomMenuItem[] = docDownloadFormats.map(
        (item: DownloadFormatType) => {
          return {
            id: item.name,
            label: `${item.name.toLocaleLowerCase() === 'media' ? 'MP3' : item.name} ${item.seqNo}`,
          } as CustomMenuItem;
        }
      );

      actionMenuItems = actionDownloadOptions;
      const menuButtonClickHandler = (e: React.MouseEvent<HTMLElement>) => {
        setDownloadMenuOpen(true);
        menuRef.current?.open(e.currentTarget as HTMLElement);
      };
      const menuItemClickHandler = (item: CustomMenuItem) => {
        handleDownloadSelection(item);
      };

      const handleDownloadSelection = async (item: CustomMenuItem) => {
        if (!documentDetails) return;
        const sequenceNumber = item.label.toString().split(' ')[1] ?? '1';
        const downloadParams: IGetDownloadDocumentParams = {
          DocId: String(documentDetails.docId),
          Type: String(item.id), // e.g., PDF, TIFF, TXT
          SeqenceNumber: sequenceNumber, // This can be dynamically set based on the document
          DisplayDocumentId: String(documentDetails.id),
        };

        try {
          // Fetch the document using the API function
          const response = await getDownloadDocument(downloadParams);

          const {blob, filename} = response;

          // Create a temporary URL for the Blob data
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename; // Use the filename from the API response
          a.click(); // Trigger the download
          window.URL.revokeObjectURL(url); // Clean up the URL object
        } catch (error) {
          console.error('Download failed', error);
          // Optionally, display a toast/alert for the user about the error
        }
      };

      const handleMenuClose = () => {
        setDownloadMenuOpen(false);
      };

      useEffect(() => {
        if (documentDetails?.id) getDocDownloadFormats();
      }, [documentDetails, getDocDownloadFormats]);

      const printDocument = async (payload: IPrintDocumentRequest) => {
        try {
          const {status} = await postPrintDocument(payload);

          if (status === 200) {
            setShowPrintToast(true);
            setTimeout(() => {
              setShowPrintToast(false);
            }, 5000);
          }
        } catch (error) {
          console.error('Print Failed!', error);
        }
      };

      const handleDocumentPrint = () => {
        // Get Print Document
        if (documentDetails) {
          const {id, docId, docExt} = documentDetails;
          const payload: IPrintDocumentRequest = {
            documentId: id,
            documentName: docId,
            docExt: docExt || 'viewer',
            isPreview: false,
          };
          printDocument(payload);
        }
      };

      return (
        <>
          <Box className="row-container medium-gap small-gap-wrapper margin-right-zero">
            <ArrMenu
              data-testid="doc-nav-menu"
              id="doc-nav-menu"
              className="doc-toolbar-action-menu"
              ref={menuRef}
              items={actionMenuItems}
              direction="bottom-right"
              subMenuDirection="left"
              onItemClick={menuItemClickHandler}
              onClose={handleMenuClose}
            />
            <ArrIconButton
              shape="default"
              icon={<PrintIcon />}
              ariaLabel={'First Page'}
              tooltipContent="Print"
              testId="print"
              onClick={handleDocumentPrint}
            />
            <ArrIconButton
              shape="default"
              icon={<DownloadIcon />}
              className={`action-icon ${downloadMenuOpen ? 'active' : ''}`}
              ariaLabel={'Previous Page'}
              tooltipContent="Download"
              testId="download"
              onClick={menuButtonClickHandler}
            />
          </Box>

          {showPrintToast && (
            <ArrToastNotification
              data-testid="print-success-toast"
              title={t('common.success')}
              message={t('docView.printSuccessMessage')}
              status={[ToastType.Success]}
              onClose={() => setShowPrintToast(false)}
            />
          )}
        </>
      );
    },
    [documentDetails]
  );
  const toolss = useMemo(
    () => [
      {
        icon: <Thumbnailicon />,
        tooltipContent: 'Thumbnails',
        onClick: () => {
          const hideThumb = () => {
            const thumbnailDiv = document.getElementById('thumbnails');
            const resizerDiv = document.getElementById('prizmdoc-resizer');

            if (thumbnailDiv && resizerDiv) {
              if (thumbnailDiv.style.display === 'none') {
                thumbnailDiv.style.display = 'block';
                resizerDiv.style.display = 'flex';
              } else {
                thumbnailDiv.style.display = 'none';
                resizerDiv.style.display = 'none';
              }
            }
            window.dispatchEvent(new Event('resize'));
            viewerControl.fitContent(window.PCCViewer.FitType.FullWidth);
          };
          hideThumb();
        },
        testId: 'toggle-thumbnails',
        ariaLabel: 'Thumbnails',
      },
      {
        icon: isPanMode ? <PanIcon /> : <CopyTextIcon />,
        tooltipContent: isPanMode ? 'Pan' : 'Select Text',
        testId: 'toggle-pan-text',

        onClick: () => {
          viewerControl.setCurrentMouseTool(
            isPanMode ? MouseToolType.SelectText : MouseToolType.PanAndEdit
          );
          setIsPanMode((prev) => !prev);
        },
        ariaLabel: isPanMode ? 'Pan' : 'Select Text',
      },
      {
        icon: <RotateLeftIcon />,
        tooltipContent: 'Rotate Left',
        onClick: () => viewerControl.rotatePage(-90),
        testId: 'rotate-left',
        ariaLabel: 'Rotate Left',
      },
      {
        icon: <RotateRightIcon />,
        tooltipContent: 'Rotate Right',
        testId: 'rotate-right',
        ariaLabel: 'Rotate Right',
        onClick: () => viewerControl.rotatePage(90),
      },
    ],
    [isPanMode, viewerControl]
  );
  return (
    <Box className="doc-toolbar-wrapper">
      <Box className="row-container-no-pad">
        <PageNavigation
          viewerControl={viewerControl}
          totalPages={totalPages}
          pageNumber={pageNumber}
        />

        {viewerControl && (
          <ZoomControls viewerControl={viewerControl} isReady={isReady} />
        )}
        <Box className="row-container medium-gap small-gap-wrapper">
          {toolss.map((tool, index) => (
            <ArrIconButton
              key={index}
              shape="default"
              icon={tool.icon}
              ariaLabel={tool.ariaLabel}
              onClick={tool.onClick}
              tooltipContent={tool.tooltipContent}
              testId={tool.testId}
            />
          ))}
        </Box>
      </Box>
      <PrintAndDownload documentDetails={documentDetails} />
    </Box>
  );
};
